import * as Cesium from "cesium";
import { computeZoomLevel } from "../MapUtility";
import { createEdgeAutoPan } from "./EdgeAutoPan";

export const drawingNewEntity = (
  viewer,
  type = "Point",
  lineSampleRateRef,
  snappingOptions = {
    vertex: true,
    midpoint: true,
    vertexTolerancePx: 20,
    midpointTolerancePx: 20,
    previewRadiusPx: 50, // kept for compatibility; actual radius is dynamic
    indexCellSizeDeg: 0.0001,
    maxPreviewMarkers: 80,
    maxVerticesPerEntityForIndex: 400,

    // Tunables:
    projectionBudgetPerFrame: 300, // max number of toWin() per frame
    prefilterFactor: 4, // prefilter N = factor * maxPreview before projection
    indexRebuildDebounceMs: 200, // debounce rebuildWorldIndex on collection changes
    mppUpdateMs: 80, // throttle meters-per-pixel recompute (ms)
    mppMoveTolPx: 10, // px move tolerance to recompute MPP
  },
  drawingOptions = {},
) => {
  // -------------------- Validate geometry type --------------------
  const validTypes = ["Point", "MultiPoint", "LineString", "MultiLineString", "Polygon", "MultiPolygon"];
  if (!validTypes.includes(type)) throw new Error("Unsupported type: " + type);
  if (!viewer || !viewer.scene) throw new Error("Viewer is required");
  const autoJumpEnabledRef = drawingOptions?.autoJumpEnabledRef;

  // -------------------- Snap flags & tolerances (same as EditInteractions) --------------------
  const SNAP_VERTEX_ENABLED = !!snappingOptions?.vertex;
  const SNAP_MID_ENABLED = !!snappingOptions?.midpoint;

  const SNAP_VERTEX_TOLERANCE_BASE = 17;
  const SNAP_ENTITYMID_TOLERANCE_BASE = 17;

  const SNAP_ZOOM_CONFIG = [
    { maxZoom: 18, gpuRadiusPx: 15, previewStickyRadiusPx: 17, tol: 1 },
    { maxZoom: 19, gpuRadiusPx: 15, previewStickyRadiusPx: 17, tol: 2 },
    { maxZoom: 20, gpuRadiusPx: 30, previewStickyRadiusPx: 35, tol: 3 },
    { maxZoom: 21, gpuRadiusPx: 35, previewStickyRadiusPx: 40, tol: 4 },
    { maxZoom: 22, gpuRadiusPx: 40, previewStickyRadiusPx: 45, tol: 5 },
    { maxZoom: 23, gpuRadiusPx: 45, previewStickyRadiusPx: 50, tol: 6 },
    { maxZoom: 24, gpuRadiusPx: 50, previewStickyRadiusPx: 55, tol: 7 },
    { maxZoom: 25, gpuRadiusPx: 60, previewStickyRadiusPx: 65, tol: 8 },
    { maxZoom: 35, gpuRadiusPx: 70, previewStickyRadiusPx: 75, tol: 8 },
  ];

  function getSnapZoomConfig() {
    const zoom = typeof computeZoomLevel === "function" ? computeZoomLevel(viewer) : 0;
    for (const cfg of SNAP_ZOOM_CONFIG) {
      if (zoom <= cfg.maxZoom) return cfg;
    }
    return SNAP_ZOOM_CONFIG[SNAP_ZOOM_CONFIG.length - 1];
  }

  function getSnapGpuRadiusPx() {
    const cfg = getSnapZoomConfig();
    return cfg.gpuRadiusPx;
  }

  function getDynamicSnapTolerancePx(basePx) {
    const cfg = getSnapZoomConfig();

    if (basePx === SNAP_VERTEX_TOLERANCE_BASE) return cfg.tol;
    if (basePx === SNAP_ENTITYMID_TOLERANCE_BASE) return cfg.tol;

    return basePx;
  }

  function getSnapPreviewStickyRadiusPx() {
    const cfg = getSnapZoomConfig();
    return cfg.previewStickyRadiusPx;
  }

  // Clamp cell size to avoid 0/NaN and pathological tiny cells
  const INDEX_CELL_DEG = Math.max(1e-6, Number(snappingOptions?.indexCellSizeDeg ?? 0.0001) || 0.0001);

  const MAX_PREVIEW = snappingOptions?.maxPreviewMarkers ?? 80;
  const MAX_VERTS_PER_ENTITY = snappingOptions?.maxVerticesPerEntityForIndex ?? 400;

  // Tunables
  const PROJECTION_BUDGET = Math.max(50, snappingOptions?.projectionBudgetPerFrame ?? 300);
  const PREFILTER_FACTOR = Math.max(1, snappingOptions?.prefilterFactor ?? 4);
  const INDEX_REBUILD_DEBOUNCE_MS = Math.max(0, snappingOptions?.indexRebuildDebounceMs ?? 200);
  const MPP_UPDATE_MS = Math.max(0, snappingOptions?.mppUpdateMs ?? 80);
  const MPP_MOVE_TOL_PX = Math.max(0, snappingOptions?.mppMoveTolPx ?? 10);

  // Hard safety caps for candidate query
  const MAX_QUERY_CELLS = 50000; // cap on grid cells per query
  const MAX_RAD_DEG = 30; // absolute deg radius cap

  // -------------------- Session state --------------------
  const segmentSampleCounts = [];
  let positions = [];
  let tempEntity;
  let tempOutlineEntity;
  const drawnEntities = [];
  const handler = new Cesium.ScreenSpaceEventHandler(viewer.canvas);

  const edgeAutoPan = createEdgeAutoPan({
    viewer,

    edgePx: 60,

    speedMin: 5,
    speedMax: 60,
    speedZGt24: 5,
    speedZ22_24: 20,
    speedZLt22: 50,

    getZoomLevel: () => computeZoomLevel(viewer),
  });

  // -------------------- Live segment preview --------------------
  let previewEntity = null;
  let previewPositions = null;
  let previewMaterial = null;

  // -------------------- Angle preview --------------------
  let angleLabelEntity = null;
  let angleArcEntity = null;
  let angleArcPositions = [];

  // -------------------- Length preview --------------------
  let lengthLabelEntity = null;

  // -------------------- Polar keyboard steering state --------------------
  let polarActive = false;
  let polarAngleDeg = 180; // default angle in degrees
  let polarRadiusMeters = NaN;
  let polarDirection = 0; // -1 or +1
  let polarTimerId = null;
  let polarCurrentTarget = null;

  let spaceDown = false;
  let commaDown = false;
  let dotDown = false;
  let aDown = false; // hold A to enable midpoint snapping/preview

  // -------------------- Snap visuals --------------------
  let midpointSnapMarker = null;
  const snapPreviewPool = { vertex: [], mid: [] };
  let snapPreviewAnchorWorld = null;

  function isMidSnapActive() {
    return SNAP_MID_ENABLED && aDown;
  }

  // -------------------- Promise contract --------------------
  let resolveFn,
    rejectFn,
    settled = false;
  const promise = new Promise((resolve, reject) => {
    resolveFn = (v) => {
      if (!settled) {
        settled = true;
        resolve(v);
      }
    };
    rejectFn = (e) => {
      if (!settled) {
        settled = true;
        reject(e);
      }
    };
  });

  // -------------------- ESC key & keyboard listeners --------------------
  let keydownListener = null;
  let keyupListener = null;

  // -------------------- RAF & mouse state --------------------
  let rafPending = false;
  let lastMoveWin = null; // {x,y}
  let lastMoveWindowPos = null; // {x,y}
  let lastMouseCartesian = null; // Cartesian3

  // -------------------- Listeners & throttles --------------------
  let dsChangedCb = null;
  let entsChangedCb = null;
  let cameraChangedCb = null;
  let lastCameraDirty = 0;
  const CAMERA_DIRTY_THROTTLE_MS = 80;

  // Debounce timer for index rebuild
  let rebuildTimer = null;

  // ======================================================================
  // Helpers: visibility and safe reads
  // ======================================================================
  function addClickMarker(pos) {
    const clickMarker = viewer.entities.add({
      position: pos,
      point: {
        pixelSize: 7,
        color: Cesium.Color.YELLOW.withAlpha(0.8),
        outlineColor: Cesium.Color.WHITE,
        outlineWidth: 2,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      },
      properties: new Cesium.PropertyBag({ isDrawingClickMarker: true, isDrawingHelper: true }),
    });
    drawnEntities.push(clickMarker);
    return clickMarker;
  }

  function densifyBetween(a, b, stepMeters) {
    // returns interior samples at fixed meter step + always includes b
    try {
      const c1 = Cesium.Cartographic.fromCartesian(a);
      const c2 = Cesium.Cartographic.fromCartesian(b);
      const geo = new Cesium.EllipsoidGeodesic(c1, c2);
      const total = geo.surfaceDistance || 0;
      if (!(stepMeters > 0) || !isFinite(total) || total <= 0) return [b];

      const n = Math.floor(total / stepMeters); // interior count
      const out = [];
      for (let i = 1; i <= n; i++) {
        const s = Math.min(i * stepMeters, total); // traveled distance
        const carto = geo.interpolateUsingSurfaceDistance(s);
        const t = total > 0 ? s / total : 1; // linear height blend
        const h = Cesium.Math.lerp(c1.height || 0, c2.height || 0, t);
        out.push(Cesium.Cartesian3.fromRadians(carto.longitude, carto.latitude, h));
      }
      out.push(b); // ensure end point
      return out;
    } catch (_) {
      return [b]; // fallback
    }
  }

  function readShow(prop, time, defaultVal = true) {
    if (prop === undefined || prop === null) return defaultVal;
    if (typeof prop?.getValue === "function") {
      const v = prop.getValue(time);
      return v === undefined || v === null ? defaultVal : !!v;
    }
    return !!prop;
  }

  function isHelperEntity(ent) {
    const p = ent?.properties;
    const get = (k) => p?.[k]?.getValue?.() === true;
    return (
      get("isControlPoint") ||
      get("isInsertPoint") ||
      get("isMidpointSnapIndicator") ||
      get("isSnapPreviewVertex") ||
      get("isSnapPreviewMid") ||
      get("isDragMeasureLine") ||
      get("isDragMeasureLabel") ||
      get("isDrawingHelper") ||
      get("isDrawingPreview") ||
      get("isDrawingTemp") ||
      get("isDrawingClickMarker")
    );
  }

  function isEntityVisible(ent, time, dsShown = true) {
    if (!dsShown) return false;
    if (!readShow(ent?.show, time, true)) return false;
    if (typeof ent?.isAvailable === "function" && !ent.isAvailable(time)) return false;

    if (ent?.polyline) {
      if (!readShow(ent.polyline?.show, time, true)) return false;
      return true;
    }
    if (ent?.polygon) {
      if (!readShow(ent.polygon?.show, time, true)) return false;
      return true;
    }
    if (ent?.position) {
      const gfx = [ent.point, ent.billboard, ent.label, ent.model];
      const anyRenderableShown = gfx.some((g) => g && readShow(g?.show, time, true));
      return anyRenderableShown;
    }
    return false;
  }

  // ======================================================================
  // Spatial index in world space (lat/lon grid)
  // ======================================================================

  const worldIndex = {
    cellDeg: INDEX_CELL_DEG,
    verts: new Map(), // Map<"ix,iy", Array<{cart,lonDeg,latDeg,ent,idx,ds}>>
    mids: new Map(), // Map<"ix,iy", Array<{cart,lonDeg,latDeg,ent,ds}>>
    totalVerts: 0,
    totalMids: 0,
  };

  function cellKey(lonDeg, latDeg) {
    const ix = Math.floor(lonDeg / worldIndex.cellDeg);
    const iy = Math.floor(latDeg / worldIndex.cellDeg);
    return `${ix},${iy}`;
  }

  function pushToCell(map, lonDeg, latDeg, rec) {
    const key = cellKey(lonDeg, latDeg);
    let arr = map.get(key);
    if (!arr) {
      arr = [];
      map.set(key, arr);
    }
    arr.push(rec);
  }

  // Compute derived height from an entity's vertices.
  // If majority of heights are ~0 return 0, otherwise return average.
  function computeEntityDerivedHeight(entity, time, excludeIndex = -1) {
    // Get positions from the entity using existing helper
    const posArr = getPositionsFromEntity(entity, time) || [];
    if (!posArr.length) return 0;

    // Convert to cartographic heights
    const heights = [];
    for (let i = 0; i < posArr.length; i++) {
      if (i === excludeIndex) continue; // skip the snapped vertex if index available
      try {
        const c = Cesium.Cartographic.fromCartesian(posArr[i]);
        heights.push(isFinite(c.height) ? c.height : 0);
      } catch (_) {
        heights.push(0);
      }
    }
    if (!heights.length) return 0;

    // Decide majority-zero (treat small values as zero)
    const ZERO_EPS = 0.5; // meters threshold for "zero"
    let zeroCount = 0;
    let sum = 0;
    for (const h of heights) {
      if (Math.abs(h) <= ZERO_EPS) zeroCount++;
      sum += h;
    }

    if (zeroCount > heights.length / 2) return 0;
    return sum / heights.length;
  }

  function forEachEntityCandidate(cb) {
    const root = viewer?.entities?.values || [];
    for (const ent of root) if (!isHelperEntity(ent)) cb(ent, null);

    const dsc = viewer?.dataSources;
    if (dsc && typeof dsc.length === "number" && typeof dsc.get === "function") {
      for (let i = 0; i < dsc.length; i++) {
        const ds = dsc.get(i);
        const ents = ds?.entities?.values || [];
        for (const ent of ents) if (!isHelperEntity(ent)) cb(ent, ds);
      }
    }
  }

  function getPositionsFromEntity(entity, time) {
    if (entity?.polyline) {
      const arr = entity.polyline.positions?.getValue?.(time);
      return Array.isArray(arr) ? arr : [];
    }
    if (entity?.polygon) {
      const h = entity.polygon.hierarchy?.getValue?.(time);
      const raw = h?.positions || [];
      const arr = [...raw];
      if (arr.length > 2 && Cesium.Cartesian3.equals(arr[0], arr[arr.length - 1])) arr.pop();
      return arr;
    }
    if (entity?.position) {
      const p = entity.position?.getValue?.(time);
      return p ? [p] : [];
    }
    return [];
  }

  function computePolylineHalfwayPoint(arr) {
    if (!arr || arr.length < 2) return null;
    let total = 0;
    const segLen = [];
    for (let i = 0; i < arr.length - 1; i++) {
      const d = Cesium.Cartesian3.distance(arr[i], arr[i + 1]);
      segLen.push(d);
      total += d;
    }
    if (total === 0) return arr[0];
    const target = total / 2;
    let acc = 0;
    for (let i = 0; i < segLen.length; i++) {
      const d = segLen[i];
      if (acc + d >= target) {
        const t = (target - acc) / (d || 1);
        const aC = Cesium.Cartographic.fromCartesian(arr[i]);
        const bC = Cesium.Cartographic.fromCartesian(arr[i + 1]);
        const lon = Cesium.Math.lerp(aC.longitude, bC.longitude, t);
        const lat = Cesium.Math.lerp(aC.latitude, bC.latitude, t);
        const h = Cesium.Math.lerp(aC.height, bC.height, t);
        return Cesium.Cartesian3.fromRadians(lon, lat, h);
      }
      acc += d;
    }
    return arr[arr.length - 1];
  }

  function computePolygonCenter(arr) {
    if (!arr?.length) return null;
    const bs = Cesium.BoundingSphere.fromPoints(arr);
    const center = bs.center;
    const cCarto = Cesium.Cartographic.fromCartesian(center);
    let sumH = 0;
    for (const p of arr) sumH += Cesium.Cartographic.fromCartesian(p).height;
    const avgH = sumH / arr.length;
    return Cesium.Cartesian3.fromRadians(cCarto.longitude, cCarto.latitude, avgH);
  }

  function getEntityAnchor(entity, time) {
    if (entity?.polyline) return computePolylineHalfwayPoint(getPositionsFromEntity(entity, time));
    if (entity?.polygon) return computePolygonCenter(getPositionsFromEntity(entity, time));
    if (entity?.position) return entity.position.getValue(time);
    return null;
  }

  function rebuildWorldIndex() {
    worldIndex.verts.clear();
    worldIndex.mids.clear();
    worldIndex.totalVerts = 0;
    worldIndex.totalMids = 0;

    const time = viewer.clock?.currentTime;

    forEachEntityCandidate((ent, ds) => {
      const dsShown = ds ? ds.show !== false : true;
      if (!isEntityVisible(ent, time, dsShown)) return;

      const pos = getPositionsFromEntity(ent, time);
      if (pos.length) {
        const last = pos.length - 1;
        const stride = Math.max(1, Math.ceil(pos.length / MAX_VERTS_PER_ENTITY));

        const pushV = (i, isEndpoint) => {
          const p = pos[i];
          const c = Cesium.Cartographic.fromCartesian(p);
          const lonDeg = Cesium.Math.toDegrees(c.longitude);
          const latDeg = Cesium.Math.toDegrees(c.latitude);
          pushToCell(worldIndex.verts, lonDeg, latDeg, { cart: p, lonDeg, latDeg, ent, idx: i, ds, isEndpoint });
          worldIndex.totalVerts++;
        };

        // Always index endpoints (for stable snap)
        pushV(0, true);
        if (last !== 0) pushV(last, true);

        // Index interior samples (as before, but mark as non-endpoint)
        for (let i = stride; i < last; i += stride) {
          pushV(i, false);
        }
      }

      const mid = getEntityAnchor(ent, time);
      if (mid) {
        const c = Cesium.Cartographic.fromCartesian(mid);
        const lonDeg = Cesium.Math.toDegrees(c.longitude);
        const latDeg = Cesium.Math.toDegrees(c.latitude);
        pushToCell(worldIndex.mids, lonDeg, latDeg, { cart: mid, lonDeg, latDeg, ent, ds });
        worldIndex.totalMids++;
      }
    });
  }

  // Build once
  rebuildWorldIndex();

  // Debounced rebuild scheduler
  function scheduleRebuild() {
    if (rebuildTimer) clearTimeout(rebuildTimer);
    rebuildTimer = setTimeout(() => {
      rebuildTimer = null;
      rebuildWorldIndex();
    }, INDEX_REBUILD_DEBOUNCE_MS);
  }

  // ======================================================================
  // Screen helpers & local candidate query
  // ======================================================================

  function toWin(cart) {
    if (!cart) return null;
    const s = viewer?.scene;
    if (!s) return null;
    const v = Cesium.SceneTransforms?.wgs84ToWindowCoordinates ? Cesium.SceneTransforms.wgs84ToWindowCoordinates(s, cart) : s.cartesianToCanvasCoordinates?.(cart);
    return v ? { x: v.x, y: v.y } : null;
  }

  function pickCartesian(win) {
    if (!win) return undefined;

    const scene = viewer?.scene;
    const cam = viewer?.camera || scene?.camera;
    if (!scene || !cam) return undefined;

    const ellipsoid = scene.globe?.ellipsoid;
    const canvas = scene.canvas || viewer.canvas;

    const w = canvas?.clientWidth || canvas?.width || 0;
    const h = canvas?.clientHeight || canvas?.height || 0;

    const x0 = Number(win.x);
    const y0 = Number(win.y);
    if (!Number.isFinite(x0) || !Number.isFinite(y0)) return undefined;

    const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
    const sx = w ? clamp(x0, 0, w - 1) : x0;
    const sy = h ? clamp(y0, 0, h - 1) : y0;

    const sp = new Cesium.Cartesian2(sx, sy);

    const isValid = (p) => {
      if (!Cesium.defined(p)) return false;
      if (!Number.isFinite(p.x) || !Number.isFinite(p.y) || !Number.isFinite(p.z)) return false;
      if (!ellipsoid) return true;
      try {
        const c = ellipsoid.cartesianToCartographic(p, new Cesium.Cartographic());
        return !!c && Number.isFinite(c.longitude) && Number.isFinite(c.latitude) && Number.isFinite(c.height);
      } catch (_) {
        return false;
      }
    };

    // 1) Globe pick (stable for ground/terrain)
    try {
      const ray = cam.getPickRay(sp);
      if (ray && scene.globe) {
        const p = scene.globe.pick(ray, scene);
        if (isValid(p)) return p;
      }
    } catch (_) {}

    // 2) Depth pick (3D tiles / depth)
    if (scene.pickPositionSupported) {
      try {
        const p = scene.pickPosition(sp);
        if (isValid(p)) return p;
      } catch (_) {}
    }

    // 3) Ellipsoid fallback
    try {
      const p = cam.pickEllipsoid(sp, ellipsoid);
      if (isValid(p)) return p;
    } catch (_) {}

    // 4) Inset retries
    const INSET = 3;
    const tries = [
      [INSET, 0],
      [-INSET, 0],
      [0, INSET],
      [0, -INSET],
      [INSET, INSET],
      [-INSET, INSET],
      [INSET, -INSET],
      [-INSET, -INSET],
    ];

    for (let i = 0; i < tries.length; i++) {
      const dx = tries[i][0];
      const dy = tries[i][1];
      const sp2 = new Cesium.Cartesian2(w ? clamp(sx + dx, 0, w - 1) : sx + dx, h ? clamp(sy + dy, 0, h - 1) : sy + dy);

      try {
        const ray = cam.getPickRay(sp2);
        if (ray && scene.globe) {
          const p = scene.globe.pick(ray, scene);
          if (isValid(p)) return p;
        }
      } catch (_) {}

      if (scene.pickPositionSupported) {
        try {
          const p = scene.pickPosition(sp2);
          if (isValid(p)) return p;
        } catch (_) {}
      }

      try {
        const p = cam.pickEllipsoid(sp2, ellipsoid);
        if (isValid(p)) return p;
      } catch (_) {}
    }

    return undefined;
  }

  function centerCameraOnCommitted(committed) {
    try {
      if (!committed) return;

      const scene = viewer?.scene;
      const cam = viewer?.camera;
      if (!scene || !cam) return;

      const ellipsoid = scene.globe?.ellipsoid;
      if (!ellipsoid) return;

      const targetCarto = ellipsoid.cartesianToCartographic(committed, new Cesium.Cartographic());
      if (!targetCarto || !Number.isFinite(targetCarto.longitude) || !Number.isFinite(targetCarto.latitude)) return;

      const canvas = scene.canvas || viewer.canvas;
      const w = canvas?.clientWidth || canvas?.width || 0;
      const h = canvas?.clientHeight || canvas?.height || 0;
      if (!w || !h) return;

      const centerSP = new Cesium.Cartesian2(w * 0.5, h * 0.5);

      let centerWorld = null;
      try {
        const ray = cam.getPickRay(centerSP);
        if (ray && scene.globe) centerWorld = scene.globe.pick(ray, scene);
      } catch (_) {}

      if (!centerWorld) {
        try {
          centerWorld = cam.pickEllipsoid(centerSP, ellipsoid);
        } catch (_) {}
      }
      if (!centerWorld) return;

      const centerCarto = ellipsoid.cartesianToCartographic(centerWorld, new Cesium.Cartographic());
      if (!centerCarto || !Number.isFinite(centerCarto.longitude) || !Number.isFinite(centerCarto.latitude)) return;

      let dLon = targetCarto.longitude - centerCarto.longitude;
      dLon = Cesium.Math.negativePiToPi(dLon);
      const dLat = targetCarto.latitude - centerCarto.latitude;

      const camCarto = cam.positionCartographic;
      if (!camCarto || !Number.isFinite(camCarto.longitude) || !Number.isFinite(camCarto.latitude) || !Number.isFinite(camCarto.height)) return;

      const newLon = Cesium.Math.negativePiToPi(camCarto.longitude + dLon);
      const newLat = Cesium.Math.clamp(camCarto.latitude + dLat, -Cesium.Math.PI_OVER_TWO + 1e-6, Cesium.Math.PI_OVER_TWO - 1e-6);

      const destination = Cesium.Cartesian3.fromRadians(newLon, newLat, camCarto.height);
      const orientation = { heading: cam.heading, pitch: cam.pitch, roll: cam.roll };

      cam.cancelFlight?.();

      const curWC = cam.positionWC;
      const moveLen = curWC ? Cesium.Cartesian3.distance(curWC, destination) : 9999;

      if (moveLen <= 2) {
        cam.setView({ destination, orientation });
      } else {
        cam.flyTo({ destination, orientation, duration: 0.35 });
      }
    } catch (_) {}
  }

  // Cached meters-per-pixel
  let lastMpp = 1.0;
  let lastMppTime = 0;
  let lastMppWin = { x: -99999, y: -99999 };

  function estimateMetersPerPixel(mouse) {
    const now = Date.now();
    const moved = Math.hypot(mouse.x - lastMppWin.x, mouse.y - lastMppWin.y);
    if (now - lastMppTime < MPP_UPDATE_MS && moved < MPP_MOVE_TOL_PX) return lastMpp;

    const p0 = pickCartesian(mouse);
    if (!p0) return lastMpp;
    const p1 = pickCartesian({ x: mouse.x + 1, y: mouse.y });
    const p2 = pickCartesian({ x: mouse.x, y: mouse.y + 1 });
    let d = 0,
      n = 0;
    if (p1) {
      d += Cesium.Cartesian3.distance(p0, p1);
      n++;
    }
    if (p2) {
      d += Cesium.Cartesian3.distance(p0, p2);
      n++;
    }
    if (n === 0) return lastMpp;

    lastMpp = d / n;
    lastMppTime = now;
    lastMppWin = { x: mouse.x, y: mouse.y };
    return lastMpp;
  }
  // short English comment: center animation token
  let centerAnimToken = 0;

  const _centerScratch = {
    win: new Cesium.Cartesian2(),
    bs: new Cesium.BoundingSphere(new Cesium.Cartesian3(), 1.0),
  };

  function centerCameraOnCommitted(committed) {
    try {
      if (autoJumpEnabledRef && autoJumpEnabledRef.current === false) return;

      const scene = viewer?.scene;
      const cam = viewer?.camera;
      if (!scene || !cam || !committed) return;

      const canvas = scene.canvas || viewer.canvas;
      const w = canvas?.clientWidth || canvas?.width || 0;
      const h = canvas?.clientHeight || canvas?.height || 0;
      if (!w || !h) return;

      // short English comment: project committed to screen
      const win = Cesium.SceneTransforms?.wgs84ToWindowCoordinates
        ? Cesium.SceneTransforms.wgs84ToWindowCoordinates(scene, committed, _centerScratch.win)
        : scene.cartesianToCanvasCoordinates?.(committed, _centerScratch.win);

      if (!win || !Number.isFinite(win.x) || !Number.isFinite(win.y)) return;

      const dxPx = win.x - w * 0.5;
      const dyPx = win.y - h * 0.5;
      if (Math.abs(dxPx) < 0.5 && Math.abs(dyPx) < 0.5) return;

      // short English comment: meters per pixel at committed
      let mpp = 0;
      try {
        _centerScratch.bs.center = committed;
        _centerScratch.bs.radius = 1.0;
        mpp = cam.getPixelSize(_centerScratch.bs, w, h);
      } catch (_) {
        mpp = 0;
      }
      if (!(mpp > 0) || !Number.isFinite(mpp)) {
        mpp = estimateMetersPerPixel({ x: win.x, y: win.y });
      }
      if (!(mpp > 0) || !Number.isFinite(mpp)) return;

      const rightMeters = dxPx * mpp;
      const upMeters = -dyPx * mpp;

      cam.cancelFlight?.();

      const STEPS = 6;
      const token = ++centerAnimToken;
      const rStep = rightMeters / STEPS;
      const uStep = upMeters / STEPS;

      const raf = typeof requestAnimationFrame === "function" ? requestAnimationFrame : (fn) => setTimeout(fn, 16);

      let i = 0;
      const stepFn = () => {
        if (token !== centerAnimToken) return;
        if (i >= STEPS) return;
        cam.moveRight(rStep);
        cam.moveUp(uStep);
        scene.requestRender?.();
        i++;
        raf(stepFn);
      };
      raf(stepFn);
    } catch (_) {}
  }

  function moveCameraAlongLastSegment() {
    try {
      if (autoJumpEnabledRef && autoJumpEnabledRef.current === false) return;
      if (!["LineString", "MultiLineString", "Polygon", "MultiPolygon"].includes(type)) return;
      if (positions.length < 2) return;

      const scene = viewer?.scene;
      const cam = viewer?.camera;
      if (!scene || !cam) return;

      const canvas = scene.canvas || viewer.canvas;
      const w = canvas?.clientWidth || canvas?.width || 0;
      const h = canvas?.clientHeight || canvas?.height || 0;
      if (!w || !h) return;

      const pA = positions[positions.length - 2];
      const pB = positions[positions.length - 1];

      const wA = toWin(pA);
      const wB = toWin(pB);
      if (!wA || !wB) return;

      const dx = wB.x - wA.x;
      const dy = wB.y - wA.y;
      const len = Math.hypot(dx, dy);
      if (!(len > 0) || !Number.isFinite(len)) return;

      let mpp = 0;
      try {
        _centerScratch.bs.center = pB;
        _centerScratch.bs.radius = 1.0;
        mpp = cam.getPixelSize(_centerScratch.bs, w, h);
      } catch (_) {
        mpp = 0;
      }
      if (!(mpp > 0) || !Number.isFinite(mpp)) {
        mpp = estimateMetersPerPixel({ x: wB.x, y: wB.y });
      }
      if (!(mpp > 0) || !Number.isFinite(mpp)) return;

      const MOVE_PX = 160;
      const rightMeters = (dx / len) * MOVE_PX * mpp;
      const upMeters = -(dy / len) * MOVE_PX * mpp;

      cam.cancelFlight?.();

      const STEPS = 6;
      const token = ++centerAnimToken;
      const rStep = rightMeters / STEPS;
      const uStep = upMeters / STEPS;

      const raf = typeof requestAnimationFrame === "function" ? requestAnimationFrame : (fn) => setTimeout(fn, 16);

      let i = 0;
      const stepFn = () => {
        if (token !== centerAnimToken) return;
        if (i >= STEPS) return;
        cam.moveRight(rStep);
        cam.moveUp(uStep);
        scene.requestRender?.();
        i++;
        raf(stepFn);
      };
      raf(stepFn);
    } catch (_) {}
  }

  // Approximate dynamic preview radius from camera height
  function getPreviewRadiusPx() {
    const h = viewer?.camera?.positionCartographic?.height;
    if (!isFinite(h)) return 10;
    const zoom = 19 - Math.log2(Math.max(1, h) / 1000);
    const z = Math.max(0, Math.min(30, zoom));
    if (z < 12) return 3;
    if (z > 20) return 25;
    return 3 + ((z - 12) * (25 - 3)) / (20 - 12);
  }

  // Safe, non-spread version + cell count guards
  function queryIndexBox(lonDeg, latDeg, radLonDeg, radLatDeg) {
    if (!isFinite(lonDeg) || !isFinite(latDeg) || !isFinite(radLonDeg) || !isFinite(radLatDeg)) {
      return { verts: [], mids: [] };
    }
    if (!(worldIndex.cellDeg > 0)) return { verts: [], mids: [] };

    let ix0 = Math.floor((lonDeg - radLonDeg) / worldIndex.cellDeg);
    let iy0 = Math.floor((latDeg - radLatDeg) / worldIndex.cellDeg);
    let ix1 = Math.floor((lonDeg + radLonDeg) / worldIndex.cellDeg);
    let iy1 = Math.floor((latDeg + radLatDeg) / worldIndex.cellDeg);

    const nx = ix1 - ix0 + 1;
    const ny = iy1 - iy0 + 1;
    if (!isFinite(nx) || !isFinite(ny) || nx <= 0 || ny <= 0 || nx * ny > MAX_QUERY_CELLS) {
      return { verts: [], mids: [] };
    }

    const verts = [];
    const mids = [];

    for (let ix = ix0; ix <= ix1; ix++) {
      for (let iy = iy0; iy <= iy1; iy++) {
        const key = `${ix},${iy}`;
        const vArr = worldIndex.verts.get(key);
        const mArr = worldIndex.mids.get(key);

        // Avoid spread (...) to prevent large-argument stack errors
        if (vArr && vArr.length) {
          for (let i = 0; i < vArr.length; i++) verts.push(vArr[i]);
        }
        if (mArr && mArr.length) {
          for (let i = 0; i < mArr.length; i++) mids.push(mArr[i]);
        }
      }
    }
    return { verts, mids };
  }

  // pick top-N by approximate degree distance (no projection yet)
  function prefilterByGeoDistance(list, lonDeg, latDeg, takeN) {
    if (!list || list.length <= takeN) return list || [];
    const cosLat = Math.cos(Cesium.Math.toRadians(latDeg));
    const scored = list.map((rec) => {
      const dx = (rec.lonDeg - lonDeg) * cosLat;
      const dy = rec.latDeg - latDeg;
      return { rec, score: dx * dx + dy * dy };
    });
    scored.sort((a, b) => a.score - b.score);
    const out = [];
    for (let i = 0; i < takeN; i++) out.push(scored[i].rec);
    return out;
  }

  function getNearbyScreenCandidates(mouse) {
    const candidates = { vertices: [], mids: [] };
    const cart = pickCartesian(mouse) || lastMouseCartesian;
    if (!cart) return candidates;

    const c = Cesium.Cartographic.fromCartesian(cart);
    const lonDeg = Cesium.Math.toDegrees(c.longitude);
    const latDeg = Cesium.Math.toDegrees(c.latitude);

    const mpp = estimateMetersPerPixel(mouse) || 1.0;

    const tolVertexPx = SNAP_VERTEX_ENABLED ? getDynamicSnapTolerancePx(SNAP_VERTEX_TOLERANCE_BASE) : 0;
    const tolMidPx = isMidSnapActive() ? getDynamicSnapTolerancePx(SNAP_ENTITYMID_TOLERANCE_BASE) : 0;

    const pxRadius = Math.max(getSnapGpuRadiusPx(), tolVertexPx, tolMidPx);

    const rMeters = pxRadius * mpp * 1.2;
    const metersPerDegLat = 111320;
    const metersPerDegLon = Math.max(1e-6, metersPerDegLat * Math.cos(Cesium.Math.toRadians(latDeg)));

    // Compute deg radii
    let radLatDeg = rMeters / metersPerDegLat;
    let radLonDeg = rMeters / metersPerDegLon;

    // Hard cap radii to avoid massive windows (e.g., near poles)
    radLatDeg = Math.min(radLatDeg, MAX_RAD_DEG);
    radLonDeg = Math.min(radLonDeg, MAX_RAD_DEG);

    // Pre-shrink if estimated cell count would exceed cap
    let nxEst = Math.max(1, Math.ceil((2 * radLonDeg) / worldIndex.cellDeg));
    let nyEst = Math.max(1, Math.ceil((2 * radLatDeg) / worldIndex.cellDeg));
    const cellsEst = nxEst * nyEst;
    if (cellsEst > MAX_QUERY_CELLS) {
      const scale = Math.sqrt(cellsEst / MAX_QUERY_CELLS);
      radLonDeg /= scale;
      radLatDeg /= scale;
    }

    const { verts, mids } = queryIndexBox(lonDeg, latDeg, radLonDeg * 1.5, radLatDeg * 1.5);

    const preN = Math.max(MAX_PREVIEW * PREFILTER_FACTOR, MAX_PREVIEW + 10);
    const vertsPref = SNAP_VERTEX_ENABLED ? prefilterByGeoDistance(verts, lonDeg, latDeg, preN) : [];
    const midsPref = isMidSnapActive() ? prefilterByGeoDistance(mids, lonDeg, latDeg, preN) : [];

    const canvas = viewer && (viewer.canvas || viewer.scene?.canvas);
    const vw = canvas?.clientWidth || viewer.scene?.drawingBufferWidth || 0;
    const vh = canvas?.clientHeight || viewer.scene?.drawingBufferHeight || 0;
    const margin = pxRadius + 32;
    const time = viewer.clock?.currentTime;

    let projBudgetLeft = PROJECTION_BUDGET;

    // Vertices
    if (SNAP_VERTEX_ENABLED && projBudgetLeft > 0) {
      for (let i = 0; i < vertsPref.length && projBudgetLeft > 0; i++) {
        const v = vertsPref[i];
        // Default: show/snap only endpoints; hold A to allow interior vertices too
        if (!aDown && v.isEndpoint === false) continue;
        const dsShown = v.ds ? v.ds.show !== false : true;
        if (!isEntityVisible(v.ent, time, dsShown)) continue;

        const p2d = toWin(v.cart);
        projBudgetLeft--;
        if (!p2d) continue;
        if (p2d.x < -margin || p2d.x > vw + margin || p2d.y < -margin || p2d.y > vh + margin) continue;

        const dx = mouse.x - p2d.x,
          dy = mouse.y - p2d.y;
        const d = Math.hypot(dx, dy);
        if (d <= pxRadius) {
          candidates.vertices.push({ p: v.cart, p2d, ent: v.ent, index: v.idx, dist: d });
          if (candidates.vertices.length >= MAX_PREVIEW && candidates.mids.length >= MAX_PREVIEW) break;
        }
      }
    }

    // Midpoints / anchors
    if (isMidSnapActive() && projBudgetLeft > 0) {
      for (let i = 0; i < midsPref.length && projBudgetLeft > 0; i++) {
        const m = midsPref[i];
        const dsShown = m.ds ? m.ds.show !== false : true;
        if (!isEntityVisible(m.ent, time, dsShown)) continue;

        const p2d = toWin(m.cart);
        projBudgetLeft--;
        if (!p2d) continue;
        if (p2d.x < -margin || p2d.x > vw + margin || p2d.y < -margin || p2d.y > vh + margin) continue;

        const dx = mouse.x - p2d.x,
          dy = mouse.y - p2d.y;
        const d = Math.hypot(dx, dy);
        if (d <= pxRadius) {
          candidates.mids.push({ p: m.cart, p2d, ent: m.ent, dist: d });
          if (candidates.vertices.length >= MAX_PREVIEW && candidates.mids.length >= MAX_PREVIEW) break;
        }
      }
    }

    candidates.vertices.sort((a, b) => a.dist - b.dist);
    candidates.mids.sort((a, b) => a.dist - b.dist);

    if (candidates.vertices.length + candidates.mids.length > MAX_PREVIEW) {
      const need = MAX_PREVIEW;
      const takeV = Math.min(candidates.vertices.length, Math.floor(need * 0.7));
      const takeM = Math.min(candidates.mids.length, need - takeV);
      candidates.vertices = candidates.vertices.slice(0, takeV);
      candidates.mids = candidates.mids.slice(0, takeM);
    }

    return candidates;
  }

  // ======================================================================
  // Snapping & preview helpers (using local candidates)
  // ======================================================================

  function findVertexSnapLocal(mouse, local) {
    if (!SNAP_VERTEX_ENABLED || !local?.vertices?.length || !mouse) return null;

    const tolPx = getDynamicSnapTolerancePx(SNAP_VERTEX_TOLERANCE_BASE);
    let best = null;

    const minx = mouse.x - tolPx;
    const maxx = mouse.x + tolPx;
    const miny = mouse.y - tolPx;
    const maxy = mouse.y + tolPx;

    for (const v of local.vertices) {
      const { x, y } = v.p2d;
      if (x < minx || x > maxx || y < miny || y > maxy) continue;

      const d = Math.hypot(mouse.x - x, mouse.y - y);
      if (d > tolPx) continue;

      if (!best || d < best.dist) {
        best = { dist: d, cartesian: v.p, vertex: v };
      }
    }
    return best;
  }

  function findMidSnapLocal(mouse, local) {
    if (!isMidSnapActive() || !local?.mids?.length || !mouse) return null;

    const tolPx = getDynamicSnapTolerancePx(SNAP_ENTITYMID_TOLERANCE_BASE);
    let best = null;

    const minx = mouse.x - tolPx;
    const maxx = mouse.x + tolPx;
    const miny = mouse.y - tolPx;
    const maxy = mouse.y + tolPx;

    for (const m of local.mids) {
      const { x, y } = m.p2d;
      if (x < minx || x > maxx || y < miny || y > maxy) continue;

      const d = Math.hypot(mouse.x - x, mouse.y - y);
      if (d > tolPx) continue;

      if (!best || d < best.dist) {
        best = { dist: d, cartesian: m.p, mid: m };
      }
    }
    return best;
  }

  function createVertexPreviewMarker() {
    const ent = viewer.entities.add({
      position: Cesium.Cartesian3.fromDegrees(0, 0, 0),
      point: {
        color: Cesium.Color.fromCssColorString("#00e5ff").withAlpha(0.95),
        pixelSize: 10,
        heightReference: Cesium.HeightReference.NONE,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        outlineColor: Cesium.Color.WHITE.withAlpha(0.9),
        outlineWidth: 1.5,
      },
      show: false,
      properties: new Cesium.PropertyBag({ isSnapPreviewVertex: true, isDrawingHelper: true }),
    });
    snapPreviewPool.vertex.push(ent);
    drawnEntities.push(ent);
    return ent;
  }

  function createMidPreviewMarker() {
    const ent = viewer.entities.add({
      position: Cesium.Cartesian3.fromDegrees(0, 0, 0),
      point: {
        color: Cesium.Color.fromCssColorString("#ff4dd2").withAlpha(0.95),
        pixelSize: 12,
        heightReference: Cesium.HeightReference.NONE,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        outlineColor: Cesium.Color.WHITE.withAlpha(0.9),
        outlineWidth: 1.5,
      },
      show: false,
      properties: new Cesium.PropertyBag({ isSnapPreviewMid: true, isDrawingHelper: true }),
    });
    snapPreviewPool.mid.push(ent);
    drawnEntities.push(ent);
    return ent;
  }

  function showMidpointSnapIndicator(position) {
    if (!midpointSnapMarker) {
      midpointSnapMarker = viewer.entities.add({
        position,
        point: {
          color: Cesium.Color.fromCssColorString("#ff69b4").withAlpha(0.95),
          pixelSize: 12,
          heightReference: Cesium.HeightReference.NONE,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          outlineColor: Cesium.Color.WHITE.withAlpha(0.9),
          outlineWidth: 1.5,
        },
        properties: new Cesium.PropertyBag({ isMidpointSnapIndicator: true, isDrawingHelper: true }),
      });
      drawnEntities.push(midpointSnapMarker);
    } else {
      midpointSnapMarker.position = position;
      midpointSnapMarker.show = true;
    }
  }

  function hideMidpointSnapIndicator() {
    if (midpointSnapMarker) midpointSnapMarker.show = false;
  }

  function hideAllSnapPreviewMarkers() {
    for (const e of snapPreviewPool.vertex) e.show = false;
    for (const e of snapPreviewPool.mid) e.show = false;
    snapPreviewAnchorWorld = null;
    hideMidpointSnapIndicator();
  }

  // -------------------- PREVIEW MARKERS UPDATE --------------------
  const MARKER_UPDATE_MS = 40;
  let lastMarkerUpdateTime = 0;

  function updateSnapPreviewMarkers(mouse, local) {
    const now = Date.now();
    const mouseWin = mouse || lastMoveWindowPos;

    if (mouseWin && lastMoveWindowPos && now - lastMarkerUpdateTime < MARKER_UPDATE_MS && Math.hypot(mouseWin.x - lastMoveWindowPos.x, mouseWin.y - lastMoveWindowPos.y) < 2) {
      return;
    }
    lastMarkerUpdateTime = now;

    if (!local) {
      hideAllSnapPreviewMarkers();
      return;
    }

    const stickyRadiusPx = getSnapPreviewStickyRadiusPx();

    const closeVertices = SNAP_VERTEX_ENABLED ? local.vertices || [] : [];
    const closeMids = isMidSnapActive() ? local.mids || [] : [];

    const hasAny = closeVertices.length > 0 || closeMids.length > 0;

    if (!hasAny) {
      if (!snapPreviewAnchorWorld || !mouseWin) {
        hideAllSnapPreviewMarkers();
        return;
      }
      const anchorWin = toWin(snapPreviewAnchorWorld);
      if (!anchorWin) {
        hideAllSnapPreviewMarkers();
        return;
      }
      const dx = anchorWin.x - mouseWin.x;
      const dy = anchorWin.y - mouseWin.y;
      const d = Math.hypot(dx, dy);
      if (d > stickyRadiusPx) {
        hideAllSnapPreviewMarkers();
      }
      return;
    }

    let anchor = null;
    if (closeVertices.length) anchor = closeVertices[0].p;
    else if (closeMids.length) anchor = closeMids[0].p;
    snapPreviewAnchorWorld = anchor;

    while (snapPreviewPool.vertex.length < closeVertices.length && snapPreviewPool.vertex.length + snapPreviewPool.mid.length < MAX_PREVIEW) {
      createVertexPreviewMarker();
    }
    while (snapPreviewPool.mid.length < closeMids.length && snapPreviewPool.vertex.length + snapPreviewPool.mid.length < MAX_PREVIEW) {
      createMidPreviewMarker();
    }

    // vertex markers
    for (let i = 0; i < snapPreviewPool.vertex.length; i++) {
      const marker = snapPreviewPool.vertex[i];
      if (i < closeVertices.length) {
        marker.position = closeVertices[i].p;
        marker.show = true;
      } else {
        marker.show = false;
      }
    }

    // mid markers
    for (let i = 0; i < snapPreviewPool.mid.length; i++) {
      const marker = snapPreviewPool.mid[i];
      if (i < closeMids.length) {
        marker.position = closeMids[i].p;
        marker.show = true;
      } else {
        marker.show = false;
      }
    }
  }

  // ======================================================================
  // Drawing visuals
  // ======================================================================

  function clearAngleVisuals() {
    if (angleLabelEntity) {
      try {
        viewer.entities.remove(angleLabelEntity);
      } catch (_) {}
      angleLabelEntity = null;
    }
    if (angleArcEntity) {
      try {
        viewer.entities.remove(angleArcEntity);
      } catch (_) {}
      angleArcEntity = null;
    }
    angleArcPositions = [];

    // reset preview color when angle is not shown
    if (previewMaterial) {
      previewMaterial.color = Cesium.Color.YELLOW.withAlpha(0.85); // base color
    }
  }

  function clearLengthVisual() {
    if (lengthLabelEntity) {
      try {
        viewer.entities.remove(lengthLabelEntity);
      } catch (_) {}
      lengthLabelEntity = null;
    }
  }

  function clearPreview() {
    if (previewEntity) {
      viewer.entities.remove(previewEntity);
      previewEntity = null;
      previewPositions = null;
    }
  }

  function updateDrawing() {
    if (tempEntity) {
      viewer.entities.remove(tempEntity);
      tempEntity = null;
    }
    if (tempOutlineEntity) {
      viewer.entities.remove(tempOutlineEntity);
      tempOutlineEntity = null;
    }
    if (["Point", "MultiPoint"].includes(type)) {
      positions.forEach((pos) => {
        const pointEntity = viewer.entities.add({
          position: pos,
          point: { pixelSize: 10, color: Cesium.Color.RED, disableDepthTestDistance: Number.POSITIVE_INFINITY },
          properties: new Cesium.PropertyBag({ isDrawingHelper: true }),
        });
        drawnEntities.push(pointEntity);
      });
    } else if (["LineString", "MultiLineString"].includes(type)) {
      tempEntity = viewer.entities.add({
        polyline: { positions, width: 2, material: Cesium.Color.YELLOW, clampToGround: false },
        properties: new Cesium.PropertyBag({ isDrawingTemp: true }),
      });
      drawnEntities.push(tempEntity);
    } else if (["Polygon", "MultiPolygon"].includes(type)) {
      if (positions.length >= 3) {
        // temp polygon: no fill
        tempEntity = viewer.entities.add({
          polygon: {
            hierarchy: new Cesium.PolygonHierarchy(positions),
            material: Cesium.Color.TRANSPARENT, // no fill
            outline: false, // outline dashed must be done with polyline
          },
          properties: new Cesium.PropertyBag({ isDrawingTemp: true }),
        });
        drawnEntities.push(tempEntity);

        // temp dashed outline as polyline
        const closed = positions.concat([positions[0]]);
        tempOutlineEntity = viewer.entities.add({
          polyline: {
            positions: closed,
            width: 2,
            material: new Cesium.PolylineDashMaterialProperty({
              color: Cesium.Color.YELLOW.withAlpha(0.9),
              dashLength: 16.0,
              gapColor: Cesium.Color.TRANSPARENT,
            }),
            clampToGround: false,
          },
          properties: new Cesium.PropertyBag({ isDrawingTemp: true, isDrawingHelper: true }),
        });
        drawnEntities.push(tempOutlineEntity);
      }
    }
  }

  function ensurePreview(lastCommitted, currentMouse) {
    if (!["LineString", "MultiLineString", "Polygon", "MultiPolygon"].includes(type)) return;
    if (positions.length < 1 || !lastCommitted || !currentMouse) return;

    if (!previewEntity) {
      previewPositions = [lastCommitted, currentMouse];

      if (!previewMaterial) {
        // create base material once
        previewMaterial = new Cesium.PolylineDashMaterialProperty({
          color: Cesium.Color.YELLOW.withAlpha(0.85),
          dashLength: 16.0,
          gapColor: Cesium.Color.TRANSPARENT,
        });
      }

      previewEntity = viewer.entities.add({
        polyline: {
          positions: new Cesium.CallbackProperty(() => previewPositions, false),
          width: 2,
          material: previewMaterial, // use shared material
          clampToGround: false,
        },
        properties: new Cesium.PropertyBag({ isDrawingPreview: true }),
      });
      drawnEntities.push(previewEntity);
    } else {
      previewPositions[0] = lastCommitted;
      previewPositions[1] = currentMouse;
    }
  }

  function updateAngleVisuals() {
    // only for line drawing
    if (!["LineString", "MultiLineString"].includes(type)) {
      clearAngleVisuals();
      return;
    }
    if (positions.length < 2 || !lastMouseCartesian) {
      clearAngleVisuals();
      return;
    }

    const p0 = positions[positions.length - 2];
    const p1 = positions[positions.length - 1];
    const p2 = lastMouseCartesian;

    // ENU frame at vertex p1 (for arc geometry)
    const enu = Cesium.Transforms.eastNorthUpToFixedFrame(p1);
    const invEnu = Cesium.Matrix4.inverse(enu, new Cesium.Matrix4());

    const lp0 = Cesium.Matrix4.multiplyByPoint(invEnu, p0, new Cesium.Cartesian3());
    const lp2 = Cesium.Matrix4.multiplyByPoint(invEnu, p2, new Cesium.Cartesian3());

    const v1 = new Cesium.Cartesian3(lp0.x, lp0.y, 0.0);
    const v2 = new Cesium.Cartesian3(lp2.x, lp2.y, 0.0);

    const mag1 = Math.hypot(v1.x, v1.y);
    const mag2 = Math.hypot(v2.x, v2.y);
    if (!isFinite(mag1) || !isFinite(mag2) || mag1 === 0 || mag2 === 0) {
      clearAngleVisuals();
      return;
    }

    // screen-space angle (what user sees)
    const w0 = toWin(p0);
    const w1 = toWin(p1);
    const w2 = toWin(p2);

    if (!w0 || !w1 || !w2) {
      clearAngleVisuals();
      return;
    }

    const a = { x: w0.x - w1.x, y: w0.y - w1.y };
    const b = { x: w2.x - w1.x, y: w2.y - w1.y };

    const magA = Math.hypot(a.x, a.y);
    const magB = Math.hypot(b.x, b.y);
    if (!isFinite(magA) || !isFinite(magB) || magA === 0 || magB === 0) {
      clearAngleVisuals();
      return;
    }

    const dot = a.x * b.x + a.y * b.y;
    const cosTheta = Cesium.Math.clamp(dot / (magA * magB), -1.0, 1.0);
    const angleRadScreen = Math.acos(cosTheta);
    const angleDegScreen = Cesium.Math.toDegrees(angleRadScreen);

    const text = angleDegScreen.toFixed(1) + "°";

    // preview color based on screen angle
    if (previewMaterial) {
      const isStraight = Math.abs(angleDegScreen - 180) < 0.01;
      previewMaterial.color = isStraight ? Cesium.Color.LIME.withAlpha(0.9) : Cesium.Color.YELLOW.withAlpha(0.85);
    }

    const radius = Math.min(mag1, mag2) * 0.5;

    // sign of rotation using 2D cross product in ENU
    const crossZ = v1.x * v2.y - v1.y * v2.x;
    const sign = crossZ >= 0 ? 1 : -1;

    const labelWorld = p1;

    // angle label
    if (!angleLabelEntity) {
      angleLabelEntity = viewer.entities.add({
        position: labelWorld,
        label: {
          text,
          font: "14px sans-serif",
          pixelOffset: new Cesium.Cartesian2(20, -25),
          fillColor: Cesium.Color.WHITE,
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 2,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
        properties: new Cesium.PropertyBag({
          isDrawingHelper: true,
          isAngleLabel: true,
        }),
      });
      drawnEntities.push(angleLabelEntity);
    } else {
      angleLabelEntity.position = labelWorld;
      angleLabelEntity.label.text = text;
    }

    // arc positions (use ENU, follow screen angle)
    angleArcPositions.length = 0; // reuse array
    const steps = 32;
    const baseAngle = Math.atan2(v1.y, v1.x);
    const arcRad = angleRadScreen;

    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const ang = baseAngle + sign * arcRad * t;
      const localPt = new Cesium.Cartesian3(Math.cos(ang) * radius, Math.sin(ang) * radius, 0.0);
      const worldPt = Cesium.Matrix4.multiplyByPoint(enu, localPt, new Cesium.Cartesian3());
      angleArcPositions.push(worldPt);
    }

    // arc entity
    if (!angleArcEntity) {
      angleArcEntity = viewer.entities.add({
        polyline: {
          positions: new Cesium.CallbackProperty(() => angleArcPositions, false),
          width: 2,
          material: Cesium.Color.CYAN,
          clampToGround: false,
        },
        properties: new Cesium.PropertyBag({
          isDrawingHelper: true,
          isAngleArc: true,
        }),
      });
      drawnEntities.push(angleArcEntity);
    }
  }

  function updateLengthVisuals() {
    if (!["LineString", "MultiLineString"].includes(type)) {
      clearLengthVisual();
      return;
    }
    if (positions.length < 1 || !lastMouseCartesian) {
      clearLengthVisual();
      return;
    }

    const p1 = positions[positions.length - 1];
    const p2 = lastMouseCartesian;

    // geodesic distance on ellipsoid (meters)
    const c1 = Cesium.Cartographic.fromCartesian(p1);
    const c2 = Cesium.Cartographic.fromCartesian(p2);
    let dist = 0;
    try {
      const geo = new Cesium.EllipsoidGeodesic(c1, c2);
      dist = geo.surfaceDistance || 0;
    } catch (_) {
      clearLengthVisual();
      return;
    }
    if (!isFinite(dist) || dist <= 0) {
      clearLengthVisual();
      return;
    }

    let text;
    if (dist < 1) {
      text = (dist * 100).toFixed(0) + " cm";
    } else {
      text = dist.toFixed(2) + " m";
    }

    // midpoint for label
    const mid = Cesium.Cartesian3.lerp(p1, p2, 0.5, new Cesium.Cartesian3());

    if (!lengthLabelEntity) {
      lengthLabelEntity = viewer.entities.add({
        position: mid,
        label: {
          text,
          font: "14px sans-serif",
          pixelOffset: new Cesium.Cartesian2(0, -20),
          fillColor: Cesium.Color.YELLOW,
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 2,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
        properties: new Cesium.PropertyBag({
          isDrawingHelper: true,
          isLengthLabel: true,
        }),
      });
      drawnEntities.push(lengthLabelEntity);
    } else {
      lengthLabelEntity.position = mid;
      lengthLabelEntity.label.text = text;
    }
  }

  function computePolarPoint(origin, angleDeg, radiusMeters) {
    // origin: Cartesian3, angleDeg in ENU, radiusMeters on local plane
    if (!origin || !isFinite(radiusMeters) || radiusMeters <= 0) return origin;
    const angRad = Cesium.Math.toRadians(angleDeg);
    const enu = Cesium.Transforms.eastNorthUpToFixedFrame(origin);
    const local = new Cesium.Cartesian3(Math.cos(angRad) * radiusMeters, Math.sin(angRad) * radiusMeters, 0.0);
    return Cesium.Matrix4.multiplyByPoint(enu, local, new Cesium.Cartesian3());
  }

  const POLAR_STEP_MS = 100; // 100 ms per step
  const POLAR_STEP_DEG = 0.5; // step size in degrees

  function handlePolarStep() {
    // only for line / poly
    if (!["LineString", "MultiLineString", "Polygon", "MultiPolygon"].includes(type)) return;
    if (positions.length < 2) return;

    const a = positions[positions.length - 2]; // previous point
    const b = positions[positions.length - 1]; // last point

    // ENU at B
    const enu = Cesium.Transforms.eastNorthUpToFixedFrame(b);
    const invEnu = Cesium.Matrix4.inverse(enu, new Cesium.Matrix4());

    // A in local ENU of B
    const la = Cesium.Matrix4.multiplyByPoint(invEnu, a, new Cesium.Cartesian3());
    const vPrev = new Cesium.Cartesian3(la.x, la.y, 0.0); // BA in local 2D
    const lenPrev = Math.hypot(vPrev.x, vPrev.y);
    if (!isFinite(lenPrev) || lenPrev === 0) return;

    // radius = length of last segment (always)
    if (!isFinite(polarRadiusMeters) || polarRadiusMeters <= 0) {
      polarRadiusMeters = lenPrev;
    }

    // first time: default angle = 180 (colinear)
    if (!polarActive) {
      polarAngleDeg = 180;
      polarActive = true;
    }

    // keyboard step
    if (polarDirection !== 0) {
      const next = polarAngleDeg + POLAR_STEP_DEG * polarDirection;
      // snap to 0.5°
      polarAngleDeg = Math.round(next * 2) / 2;
    }

    // rotate BA by polarAngleDeg (CCW) → interior angle = polarAngleDeg
    const theta = Cesium.Math.toRadians(polarAngleDeg);
    const cosT = Math.cos(theta);
    const sinT = Math.sin(theta);
    const ux = vPrev.x / lenPrev;
    const uy = vPrev.y / lenPrev;

    const dirX = ux * cosT - uy * sinT;
    const dirY = ux * sinT + uy * cosT;

    const lc = new Cesium.Cartesian3(
      dirX * polarRadiusMeters,
      dirY * polarRadiusMeters,
      la.z, // keep similar height
    );

    const target = Cesium.Matrix4.multiplyByPoint(enu, lc, new Cesium.Cartesian3());

    polarCurrentTarget = target;
    lastMouseCartesian = target;

    const lastCommitted = b;
    ensurePreview(lastCommitted, target);

    if (["LineString", "MultiLineString"].includes(type)) {
      updateAngleVisuals();
      updateLengthVisuals();
    }

    try {
      viewer.scene.requestRender?.();
    } catch (_) {}
  }

  function startPolarTimer() {
    if (!polarDirection) return;
    if (polarTimerId) return;
    // do one immediate step
    handlePolarStep();
    polarTimerId = setInterval(handlePolarStep, POLAR_STEP_MS);
  }

  function stopPolarTimer() {
    if (polarTimerId) {
      clearInterval(polarTimerId);
      polarTimerId = null;
    }
    polarDirection = 0;
  }

  function undoLastPoint() {
    if (!positions.length) return;
    positions.pop();

    // remove last click marker
    for (let i = drawnEntities.length - 1; i >= 0; i--) {
      const e = drawnEntities[i];
      const props = e?.properties;
      const isClickMarker = props?.isDrawingClickMarker?.getValue?.() === true;
      if (isClickMarker) {
        viewer.entities.remove(e);
        drawnEntities.splice(i, 1);
        break;
      }
    }

    if (["LineString", "MultiLineString", "Polygon", "MultiPolygon"].includes(type)) {
      updateDrawing();
      clearPreview();
      hideAllSnapPreviewMarkers();
      clearAngleVisuals();
      clearLengthVisual();
      return;
    }

    if (type === "MultiPoint") {
      for (let i = drawnEntities.length - 1; i >= 0; i--) {
        const e = drawnEntities[i];
        const props = e?.properties;
        const isHelper = props?.isDrawingHelper?.getValue?.() === true;
        const isSnapPreview = props?.isSnapPreviewVertex?.getValue?.() === true || props?.isSnapPreviewMid?.getValue?.() === true || props?.isMidpointSnapIndicator?.getValue?.() === true;
        if (e?.point && isHelper && !isSnapPreview) {
          viewer.entities.remove(e);
          drawnEntities.splice(i, 1);
          break;
        }
      }
    }
  }

  function cleanupListenersAndTimers() {
    drawingActive = false;
    lastMoveWin = null;
    lastMoveWindowPos = null;
    rafPending = false;
    try {
      edgeAutoPan.destroy();
    } catch (_) {}
    try {
      handler && !handler.isDestroyed() && handler.destroy();
    } catch (_) {}
    if (keydownListener) {
      try {
        window.removeEventListener("keydown", keydownListener, true);
      } catch (_) {}
      keydownListener = null;
    }
    if (keyupListener) {
      try {
        window.removeEventListener("keyup", keyupListener, true);
      } catch (_) {}
      keyupListener = null;
    }
    try {
      viewer?.dataSources?.collectionChanged?.removeEventListener?.(dsChangedCb);
    } catch (_) {}
    try {
      viewer?.entities?.collectionChanged?.removeEventListener?.(entsChangedCb);
    } catch (_) {}
    try {
      viewer?.camera?.changed?.removeEventListener?.(cameraChangedCb);
    } catch (_) {}
    if (rebuildTimer) {
      clearTimeout(rebuildTimer);
      rebuildTimer = null;
    }
    stopPolarTimer(); // stop polar timer too
  }

  function cleanupGraphics() {
    clearPreview();
    hideAllSnapPreviewMarkers();
    clearAngleVisuals();
    clearLengthVisual();
    try {
      if (tempEntity) viewer.entities.remove(tempEntity);
    } catch (_) {}
    tempEntity = null;
    for (const e of drawnEntities.splice(0)) {
      try {
        viewer.entities.remove(e);
      } catch (_) {}
    }
  }

  const finishDrawing = () => {
    cleanupListenersAndTimers();

    const isLineOrPoly = ["LineString", "MultiLineString", "Polygon", "MultiPolygon"].includes(type);
    const isPointOrMulti = ["Point", "MultiPoint"].includes(type);

    if (isLineOrPoly && positions.length < (type.includes("Polygon") ? 3 : 2)) {
      cleanupGraphics();
      return rejectFn("Not enough points for drawing: " + type);
    }
    if (isPointOrMulti && positions.length < 1) {
      cleanupGraphics();
      return rejectFn("Not enough points for drawing: " + type);
    }

    const coords = positions.map((pos) => {
      const c = Cesium.Cartographic.fromCartesian(pos);
      return [Cesium.Math.toDegrees(c.longitude), Cesium.Math.toDegrees(c.latitude)];
    });

    let geometry = { type, coordinates: [] };
    if (type === "Point") geometry.coordinates = coords[0];
    else if (type === "MultiPoint") geometry.coordinates = coords;
    else if (type === "LineString") geometry.coordinates = coords;
    else if (type === "MultiLineString") geometry.coordinates = [coords];
    else if (type === "Polygon") geometry.coordinates = [coords.concat([coords[0]])];
    else if (type === "MultiPolygon") geometry.coordinates = [[coords.concat([coords[0]])]];

    const geojson = { type: "Feature", geometry, properties: {} };

    cleanupGraphics();
    resolveFn({ geojson });
  };

  function commitPolarCurrentTarget() {
    if (!polarCurrentTarget) return;

    const committed = polarCurrentTarget;

    const step = Number(lineSampleRateRef?.current) || 0;
    let pushedCount = 0;

    if (step > 0 && ["LineString", "MultiLineString", "Polygon", "MultiPolygon"].includes(type) && positions.length >= 1) {
      const samples = densifyBetween(positions[positions.length - 1], committed, step);
      positions.push(...samples); // include committed as last
      for (const p of samples) addClickMarker(p); // add a marker for each sample
      pushedCount = samples.length;
    } else {
      positions.push(committed);
      addClickMarker(committed); // single marker
      pushedCount = 1;
    }
    segmentSampleCounts.push(pushedCount);
    updateDrawing();

    // update previews
    lastMouseCartesian = committed;
    if (["LineString", "MultiLineString"].includes(type)) {
      updateAngleVisuals();
      updateLengthVisuals();
    }

    if (positions.length >= 2) {
      const pA = positions[positions.length - 2];
      const pB = positions[positions.length - 1];
      polarRadiusMeters = Cesium.Cartesian3.distance(pA, pB);
    }

    moveCameraAlongLastSegment();

    // reset angle session: next time again start from 180
    polarAngleDeg = 180;
    polarActive = false;
    polarCurrentTarget = null;
  }

  // ======================================================================
  // Input events (local candidate query)
  // ======================================================================

  let lastClickTime = 0;

  handler.setInputAction((click) => {
    const mouse = { x: click.position.x, y: click.position.y };
    const now = Date.now();
    if (now - lastClickTime < 250) return; // guard double click
    lastClickTime = now;

    const local = getNearbyScreenCandidates(mouse);

    const vBest = findVertexSnapLocal(mouse, local);
    let snapped = null;
    if (vBest) {
      const carto = Cesium.Cartographic.fromCartesian(vBest.cartesian);
      const h = Math.max(0, Number(carto.height) || 0);
      snapped = Cesium.Cartesian3.fromRadians(carto.longitude, carto.latitude, h);
    } else {
      const mBest = findMidSnapLocal(mouse, local);
      if (mBest) {
        const carto = Cesium.Cartographic.fromCartesian(mBest.cartesian);
        const h = Math.max(0, Number(carto.height) || 0);
        snapped = Cesium.Cartesian3.fromRadians(carto.longitude, carto.latitude, h);
      }
    }

    const raw = pickCartesian(click.position);
    const committed = snapped || raw;
    if (!committed) return;

    const step = Number(lineSampleRateRef?.current) || 0;
    let pushedCount = 0;

    if (step > 0 && ["LineString", "MultiLineString", "Polygon", "MultiPolygon"].includes(type) && positions.length >= 1) {
      const samples = densifyBetween(positions[positions.length - 1], committed, step);
      positions.push(...samples); // include committed as last
      for (const p of samples) addClickMarker(p); // add a marker for each sample
      pushedCount = samples.length;
    } else {
      positions.push(committed);
      addClickMarker(committed); // single marker
      pushedCount = 1;
    }
    segmentSampleCounts.push(pushedCount);
    updateDrawing();

    if (positions.length >= 2) {
      moveCameraAlongLastSegment();
    }
    polarRadiusMeters = NaN;
    polarAngleDeg = 180;
    polarActive = false;
    polarCurrentTarget = null;

    if (type === "Point") finishDrawing();
  }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

  handler.setInputAction(() => {
    if (type !== "Point") finishDrawing();
  }, Cesium.ScreenSpaceEventType.RIGHT_CLICK);

  let drawingActive = true;

  function processMouseFrame() {
    if (!drawingActive) return;
    rafPending = false;
    const win = lastMoveWin;
    if (!win) {
      hideAllSnapPreviewMarkers();
      clearAngleVisuals();
      clearLengthVisual();
      return;
    }

    const mouse = { x: win.x, y: win.y };
    edgeAutoPan.setMousePosition(mouse);
    edgeAutoPan.start();
    const local = getNearbyScreenCandidates(mouse);

    let current = null;
    const vBest = findVertexSnapLocal(mouse, local);
    if (vBest) {
      const carto = Cesium.Cartographic.fromCartesian(vBest.cartesian);
      const h = Math.max(0, Number(carto.height) || 0);
      current = Cesium.Cartesian3.fromRadians(carto.longitude, carto.latitude, h);
      hideMidpointSnapIndicator();
    } else {
      const mBest = findMidSnapLocal(mouse, local);
      if (mBest) {
        const carto = Cesium.Cartographic.fromCartesian(mBest.cartesian);
        const h = Math.max(0, Number(carto.height) || 0);
        current = Cesium.Cartesian3.fromRadians(carto.longitude, carto.latitude, h);
        showMidpointSnapIndicator(current);
      } else {
        hideMidpointSnapIndicator();
      }
    }

    if (!current) {
      const raw = pickCartesian(mouse);
      if (!raw) {
        hideAllSnapPreviewMarkers();
        return;
      }
      current = raw;
    }

    lastMouseCartesian = current;
    lastMoveWindowPos = mouse;

    if (["LineString", "MultiLineString", "Polygon", "MultiPolygon"].includes(type) && positions.length >= 1) {
      const lastCommitted = positions[positions.length - 1];
      ensurePreview(lastCommitted, current);
    }

    if (["LineString", "MultiLineString"].includes(type)) {
      updateAngleVisuals();
      updateLengthVisuals();
    }

    updateSnapPreviewMarkers(mouse, local);

    try {
      viewer.scene.requestRender?.();
    } catch (_) {}
  }

  handler.setInputAction((movement) => {
    const win = movement.endPosition;
    if (!win) {
      hideAllSnapPreviewMarkers();
      return;
    }
    lastMoveWin = { x: win.x, y: win.y };
    edgeAutoPan.setMousePosition(lastMoveWin);
    edgeAutoPan.start();

    if (!rafPending) {
      rafPending = true;
      requestAnimationFrame(processMouseFrame);
    }
  }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

  handler.setInputAction((click) => {
    click?.preventDefault?.();
  }, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);

  keydownListener = (e) => {
    const code = e.code;
    const key = e.key;

    if (code === "Backspace" || key === "Backspace") {
      e.preventDefault?.();
      e.stopPropagation?.();
      undoLastPoint();
      return;
    }

    if (code === "Escape" || key === "Escape") {
      e.preventDefault?.();
      e.stopPropagation?.();

      clearPreview();

      // remove angle arc + angle label
      clearAngleVisuals();

      // remove length label
      clearLengthVisual();

      // remove snap previews
      hideAllSnapPreviewMarkers();

      cleanupListenersAndTimers();

      cleanupGraphics();
      rejectFn("Drawing canceled");
      return;
    }

    // track space
    if (code === "Space" || key === " ") {
      spaceDown = true;
      return;
    }

    // Hold A to enable midpoint snap/preview (default: endpoints only)
    if (code === "KeyA" || key === "a" || key === "A") {
      aDown = true;
      if (lastMoveWin && !rafPending) {
        rafPending = true;
        requestAnimationFrame(processMouseFrame);
      }
      return;
    }

    // Enter = confirm current polar target
    if (code === "Enter" || key === "Enter") {
      e.preventDefault?.();
      e.stopPropagation?.();

      if (!polarCurrentTarget) {
        polarDirection = 0;
        handlePolarStep();
      }

      commitPolarCurrentTarget();
      return;
    }

    // Space + ,  => one direction
    if (key === "," || code === "Comma") {
      commaDown = true;
      if (spaceDown) {
        e.preventDefault?.();
        e.stopPropagation?.();
        polarDirection = -1; // choose direction
        startPolarTimer();
      }
      return;
    }

    // Space + .  => opposite direction
    if (key === "." || code === "Period") {
      dotDown = true;
      if (spaceDown) {
        e.preventDefault?.();
        e.stopPropagation?.();
        polarDirection = +1; // opposite direction
        startPolarTimer();
      }
      return;
    }
  };

  keyupListener = (e) => {
    const code = e.code;
    const key = e.key;

    if (code === "Space" || key === " ") {
      spaceDown = false;
      stopPolarTimer();
      return;
    }

    if (code === "KeyA" || key === "a" || key === "A") {
      aDown = false;
      // Immediately hide midpoint visuals when A is released
      for (const e of snapPreviewPool.mid) e.show = false;
      hideMidpointSnapIndicator();
      try {
        viewer.scene.requestRender?.();
      } catch (_) {}
      return;
    }

    if (key === "," || code === "Comma") {
      commaDown = false;
      if (!dotDown) stopPolarTimer();
      return;
    }

    if (key === "." || code === "Period") {
      dotDown = false;
      if (!commaDown) stopPolarTimer();
      return;
    }
  };

  window.addEventListener("keydown", keydownListener, true);
  window.addEventListener("keyup", keyupListener, true);

  // -------------------- Rebuild index when structure changes (debounced) --------------------
  try {
    dsChangedCb = () => {
      scheduleRebuild();
    };
    viewer?.dataSources?.collectionChanged?.addEventListener?.(dsChangedCb);
  } catch (_) {}
  try {
    entsChangedCb = () => {
      scheduleRebuild();
    };
    viewer?.entities?.collectionChanged?.addEventListener?.(entsChangedCb);
  } catch (_) {}

  // Camera changes: schedule a visual update
  try {
    cameraChangedCb = () => {
      const now = Date.now();
      if (now - lastCameraDirty > CAMERA_DIRTY_THROTTLE_MS) {
        lastCameraDirty = now;
        if (lastMoveWin && !rafPending) {
          rafPending = true;
          requestAnimationFrame(processMouseFrame);
        }
        try {
          viewer.scene.requestRender?.();
        } catch (_) {}
      }
    };
    viewer?.camera?.changed?.addEventListener?.(cameraChangedCb);
  } catch (_) {}

  return { handler, promise, finishDrawing };
};
